import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";

const RECIPIENT = "deniscatapanomixing@gmail.com";

export const Contact = () => {
    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        email: "",
        about_project: "",
        message: "",
    });

    const update = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = (e) => {
        e.preventDefault();
        if (
            !form.first_name ||
            !form.last_name ||
            !form.email ||
            !form.about_project ||
            !form.message
        ) {
            toast.error("Please fill in every field before sending.");
            return;
        }

        const subject = `New project enquiry — ${form.first_name} ${form.last_name} · ${form.about_project}`;
        const body =
            `Name: ${form.first_name} ${form.last_name}\n` +
            `Email: ${form.email}\n` +
            `About the project: ${form.about_project}\n\n` +
            `Message:\n${form.message}\n`;

        const mailto = `mailto:${RECIPIENT}?subject=${encodeURIComponent(
            subject
        )}&body=${encodeURIComponent(body)}`;

        window.location.href = mailto;
        toast.success("Opening your email app…");
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="px-6 lg:px-12 py-24 lg:py-32 border-t border-black/15"
        >
            <div className="max-w-[1440px] mx-auto">
                {/* Heading */}
                <div className="grid grid-cols-12 gap-y-10 lg:gap-x-8 items-end mb-14 lg:mb-20">
                    <div className="col-span-12 lg:col-span-3">
                        <p className="overline" data-testid="contact-overline">
                            (04) — Online Mix &amp; Mastering
                        </p>
                    </div>
                    <div className="col-span-12 lg:col-span-9">
                        <h2
                            data-testid="contact-title"
                            className="font-serif-display text-5xl sm:text-6xl lg:text-7xl tracking-[-0.02em] leading-[0.95] text-ink"
                        >
                            Online mixing &amp; <span className="italic font-light">mastering services.</span>
                        </h2>
                        <p
                            data-testid="contact-subtitle"
                            className="mt-6 font-sans text-lg text-quiet max-w-2xl leading-relaxed"
                        >
                            If you&rsquo;re ready to take your Album, EP or record to the next level, you can submit your music for online mixing and mastering.
                        </p>
                    </div>
                </div>

                {/* Body */}
                <div className="grid grid-cols-12 gap-y-14 lg:gap-x-12">
                    {/* Form */}
                    <form
                        onSubmit={submit}
                        data-testid="contact-form"
                        className="col-span-12 lg:col-span-8 lg:pl-10 lg:border-l lg:border-black/15"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                            <Field
                                label="First name"
                                id="first_name"
                                testid="contact-first-name"
                                value={form.first_name}
                                onChange={update("first_name")}
                                placeholder="Your first name"
                            />
                            <Field
                                label="Last name"
                                id="last_name"
                                testid="contact-last-name"
                                value={form.last_name}
                                onChange={update("last_name")}
                                placeholder="Your last name"
                            />
                        </div>

                        <Field
                            label="Email"
                            id="email"
                            type="email"
                            testid="contact-email"
                            value={form.email}
                            onChange={update("email")}
                            placeholder="you@artist.com"
                        />

                        <Field
                            label="About the project"
                            id="about_project"
                            testid="contact-about-project"
                            value={form.about_project}
                            onChange={update("about_project")}
                            placeholder="Single / EP / Album"
                        />

                        <div className="pt-8 pb-3 border-b border-black/20">
                            <label
                                htmlFor="message"
                                className="block font-mono text-[10px] tracking-[0.22em] uppercase text-quiet mb-3"
                            >
                                Message
                            </label>
                            <textarea
                                id="message"
                                data-testid="contact-message"
                                value={form.message}
                                onChange={update("message")}
                                rows={5}
                                placeholder="Tell me about the project"
                                className="w-full bg-transparent border-0 px-0 py-1 font-sans text-base text-ink placeholder:text-quiet/60 focus:outline-none resize-none"
                            />
                        </div>

                        <button
                            type="submit"
                            data-testid="contact-submit"
                            className="mt-10 group inline-flex items-center justify-between gap-6 bg-ink text-bone px-7 py-4 font-mono text-[11px] tracking-[0.24em] uppercase hover:bg-bone hover:text-ink hover:ring-1 hover:ring-ink transition-all"
                        >
                            Send
                            <ArrowUpRight
                                size={16}
                                className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                            />
                        </button>
                    </form>
                </div>
            </div>
        </section>
    );
};

const Field = ({ label, id, type = "text", value, onChange, placeholder, testid }) => (
    <div className="pt-8 pb-3 border-b border-black/20">
        <label
            htmlFor={id}
            className="block font-mono text-[10px] tracking-[0.22em] uppercase text-quiet mb-3"
        >
            {label}
        </label>
        <input
            id={id}
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            data-testid={testid}
            className="w-full bg-transparent border-0 px-0 py-1 font-sans text-base text-ink placeholder:text-quiet/60 focus:outline-none"
        />
    </div>
);

export default Contact;
